<template>
  <transition name="api-tutorial-fade">
    <div v-if="visible" class="api-tutorial" role="dialog" aria-modal="true" aria-label="创建交易所 API 图文教程">
      <div class="api-tutorial__backdrop" @click.stop />
      <section class="api-tutorial__panel">
        <header class="api-tutorial__header">
          <div class="api-tutorial__heading">
            <span class="api-tutorial__book" aria-hidden="true">▤</span>
            <div>
              <h2>创建交易所 API 图文教程</h2>
              <p>按步骤创建只读、交易权限的 API，再返回 UPlus 完成绑定</p>
            </div>
          </div>
          <button class="api-tutorial__close" type="button" aria-label="关闭教程" @click="$emit('close')">×</button>
        </header>

        <nav class="api-tutorial__exchanges" aria-label="选择交易所教程">
          <button
            v-for="exchange in exchanges"
            :key="exchange.robotType"
            type="button"
            :class="['api-tutorial__exchange', { active: exchange.robotType === activeRobotType }]"
            @click="selectExchange(exchange.robotType)"
          >
            <span class="api-tutorial__exchange-logo"><img :src="exchange.logo" :alt="`${exchange.name} Logo`" /></span>
            <span><strong>{{ exchange.name }}</strong><small>{{ exchange.caption }}</small></span>
            <i>{{ exchange.robotType === activeRobotType ? '查看中' : '查看教程' }}</i>
          </button>
        </nav>

        <div class="api-tutorial__body">
          <aside class="api-tutorial__aside">
            <div class="api-tutorial__current">
              <span class="api-tutorial__exchange-logo"><img :src="currentExchange.logo" :alt="`${currentExchange.name} Logo`" /></span>
              <div><small>当前教程</small><strong>{{ currentExchange.name }}</strong></div>
            </div>
            <button
              v-for="(step, index) in currentExchange.steps"
              :key="`${activeRobotType}-${index}`"
              type="button"
              @click="scrollToStep(index)"
            >
              <span>{{ index + 1 }}</span>{{ step.shortTitle || step.title }}
            </button>
            <div class="api-tutorial__safe">
              <strong>安全提醒</strong>
              <p>只开启“只读”和“交易”权限，严禁开启提现权限。</p>
            </div>
          </aside>

          <main ref="content" class="api-tutorial__content">
            <section class="api-tutorial__intro">
              <div>
                <span>{{ currentExchange.name }} API</span>
                <h3>开始创建并绑定 API</h3>
                <p>{{ currentExchange.intro }}</p>
              </div>
              <div class="api-tutorial__permission">
                <small>UPlus 所需权限</small>
                <p><b>✓ 只读</b><b>✓ 交易</b><b class="disabled">× 提现</b></p>
              </div>
            </section>

            <article
              v-for="(step, index) in currentExchange.steps"
              :key="`${activeRobotType}-step-${index}`"
              :ref="`step-${index}`"
              class="api-tutorial__step"
            >
              <div class="api-tutorial__step-title">
                <span>步骤 {{ index + 1 }}</span>
                <div><h3>{{ step.title }}</h3><p>{{ step.description }}</p></div>
              </div>
              <p v-if="step.notice" class="api-tutorial__notice">{{ step.notice }}</p>
              <div v-if="step.images && step.images.length" class="api-tutorial__images">
                <figure v-for="(picture, pictureIndex) in step.images" :key="picture.src">
                  <img :src="picture.src" :alt="picture.alt || `${currentExchange.name} 教程步骤 ${index + 1}-${pictureIndex + 1}`" />
                  <figcaption v-if="picture.caption">{{ picture.caption }}</figcaption>
                </figure>
              </div>
            </article>

            <section class="api-tutorial__finish">
              <span>✓</span>
              <div>
                <h3>返回 UPlus 完成绑定</h3>
                <p>{{ currentExchange.finish }}</p>
              </div>
            </section>
          </main>
        </div>

        <footer class="api-tutorial__footer">
          <p><span>●</span> 教程图片沿用 UPlus 原项目，交易所页面更新时请以交易所实际页面为准。</p>
          <button type="button" @click="$emit('close')">我知道了，返回绑定</button>
        </footer>
      </section>
    </div>
  </transition>
</template>

<script>
import binanceLogo from '@/assets/exchanges/binance.png'
import okxLogo from '@/assets/exchanges/okx.png'
import gateLogo from '@/assets/exchanges/gate.png'
import bybitLogo from '@/assets/exchanges/bybit.png'

const image = name => require(`@/assets/tutorial/api/${name}`)
const pictures = (...names) => names.map(name => ({ src: image(name) }))

const EXCHANGES = [
  {
    robotType: 0,
    name: 'Binance',
    caption: '币安 API',
    logo: binanceLogo,
    intro: '进入 Binance API 管理页创建 API，完成安全验证后配置现货及合约交易权限。',
    finish: '在绑定弹框中填写 API Key、Secret Key 和 6 位邮箱验证码，然后确认绑定。',
    steps: [
      {
        shortTitle: '创建 API',
        title: '进入 API 管理并创建 API',
        description: '登录 Binance 后进入 API 管理，点击“创建 API”，并按页面要求完成安全验证。',
        images: pictures('2_3_1.png', '2_3_1_1.png')
      },
      {
        shortTitle: '配置权限',
        title: '勾选 UPlus 所需交易权限',
        description: '按图开启读取、现货交易和合约交易权限；不要开启提现权限。',
        images: pictures('2_3_2_1.png', '2_3_2_2.png')
      },
      {
        shortTitle: '保存密钥',
        title: '复制并妥善保存 API Key',
        description: 'API Secret 通常只展示一次，请先保存后再回到 UPlus 填写。',
        notice: '如果创建的是加密密钥或无法获得完整 Secret，请删除后重新创建普通 API。',
        images: pictures('2_3_4.png', '2_3_5.png')
      },
      {
        shortTitle: '设置 IP',
        title: '按提示设置 IP 白名单',
        description: '交易所提示需要绑定 IP 时，按下图进入访问限制并填写系统提供的 IP。',
        images: pictures('binance_api_ip_1.png', 'binance_api_ip_2.png', 'binance_api_ip_3.png')
      }
    ]
  },
  {
    robotType: 1,
    name: 'OKX',
    caption: '欧易 API',
    logo: okxLogo,
    intro: '创建 API 时选择“绑定第三方 App”，应用程序选择 Uplus智能交易系统，并开启只读、交易权限。',
    finish: '在绑定弹框中填写 API Key、Secret Key、创建时设置的 Passphrase 和 6 位邮箱验证码。',
    steps: [
      {
        shortTitle: '选择用途',
        title: '将 API 用途设置为 API 交易',
        description: '在 OKX 创建 API 的用途选项中选择“API 交易”。',
        images: pictures('2_2_3__1.png')
      },
      {
        shortTitle: '绑定应用',
        title: '选择绑定第三方 App',
        description: '用途选择“绑定第三方 App”，然后在应用程序中查找 UPlus。',
        images: pictures('2_2_3__2.png', '2_2_3__3.png')
      },
      {
        shortTitle: '选择 UPlus',
        title: '选择 Uplus智能交易系统',
        description: '在应用程序列表中选择“Uplus智能交易系统”。',
        images: pictures('2_2_3__4.png')
      },
      {
        shortTitle: '配置权限',
        title: '只勾选只读和交易权限',
        description: '权限选择“只读”和“交易”，不要开启提现权限。',
        images: pictures('2_2_3__5.png')
      }
    ]
  },
  {
    robotType: 2,
    name: 'Gate.io',
    caption: 'Gate API',
    logo: gateLogo,
    intro: '先将 Gate.io 账户升级为统一账户，再创建用于 UPlus 查询与交易的 API。',
    finish: '在绑定弹框中填写 Gate.io API Key、Secret Key 和 6 位邮箱验证码，然后确认绑定。',
    steps: [
      {
        shortTitle: '统一账户',
        title: '升级为统一账户',
        description: '登录 Gate.io 后按图进入账户升级流程，先完成统一账户升级。',
        images: pictures('gate_1_1.png', 'gate_1_2.png', 'gate_1_3.png')
      },
      {
        shortTitle: '创建 API',
        title: '创建 Gate.io API',
        description: '进入 API 管理创建密钥，并按图设置查询与交易权限。',
        notice: '不要开启提现权限；创建完成后立即保存 API Key 与 Secret Key。',
        images: pictures('gate_2_1.png', 'gate_2_2.png', 'gate_2_3.png')
      }
    ]
  },
  {
    robotType: 3,
    name: 'Bybit',
    caption: 'Bybit API',
    logo: bybitLogo,
    intro: '从 Bybit 首页搜索 API，创建系统生成的 API Key，并配置查询与交易权限。',
    finish: '在绑定弹框中填写 Bybit API Key、API Secret 和 6 位邮箱验证码，然后确认绑定。',
    steps: [
      {
        shortTitle: '搜索 API',
        title: '在首页搜索 API',
        description: '登录 Bybit，在首页搜索“API”，点击搜索结果进入 API 管理页。',
        images: pictures('bybit_1.png')
      },
      {
        shortTitle: '创建密钥',
        title: '打开 API 创建页面',
        description: '点击“创建 API 密钥”，再点击“Create New Key”。',
        images: pictures('bybit_2.png', 'bybit_3.png')
      },
      {
        shortTitle: '密钥类型',
        title: '选择系统生成的 API 密钥',
        description: '选择“System-generated API keys”继续创建。',
        images: pictures('bybit_4.png')
      },
      {
        shortTitle: '配置权限',
        title: '配置 API 用途与权限',
        description: '按图开启读取与交易所需权限，严禁开启提现权限。',
        images: pictures('bybit_5.png', 'bybit_6.png', 'bybit_7.png')
      }
    ]
  }
]

export default {
  name: 'ApiBindingTutorialModal',
  props: {
    visible: { type: Boolean, default: false },
    robotType: { type: [Number, String], default: 0 }
  },
  data() {
    return {
      exchanges: EXCHANGES,
      activeRobotType: Number(this.robotType) || 0
    }
  },
  computed: {
    currentExchange() {
      return this.exchanges.find(item => item.robotType === this.activeRobotType) || this.exchanges[0]
    }
  },
  watch: {
    visible(value) {
      if (value) this.selectExchange(Number(this.robotType) || 0)
    },
    robotType(value) {
      if (this.visible) this.selectExchange(Number(value) || 0)
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    selectExchange(robotType) {
      const nextType = Number(robotType)
      this.activeRobotType = this.exchanges.some(item => item.robotType === nextType) ? nextType : 0
      this.$nextTick(() => {
        if (this.$refs.content) this.$refs.content.scrollTop = 0
      })
    },
    scrollToStep(index) {
      const refs = this.$refs[`step-${index}`]
      const target = Array.isArray(refs) ? refs[0] : refs
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    handleKeydown(event) {
      if (this.visible && event.key === 'Escape') this.$emit('close')
    }
  }
}
</script>

<style scoped>
.api-tutorial {
  position: fixed;
  z-index: 3300;
  inset: 0;
  display: grid;
  padding: 24px;
  place-items: center;
}

.api-tutorial__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(16, 24, 40, .58);
}

.api-tutorial__panel {
  position: relative;
  z-index: 1;
  display: flex;
  width: min(1180px, calc(100vw - 48px));
  height: min(820px, calc(100vh - 48px));
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e5e9f1;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 28px 90px rgba(15, 23, 42, .28);
}

.api-tutorial__header {
  display: flex;
  min-height: 82px;
  padding: 18px 24px;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e9edf4;
}

.api-tutorial__heading,
.api-tutorial__current,
.api-tutorial__exchange,
.api-tutorial__step-title,
.api-tutorial__finish {
  display: flex;
  align-items: center;
}

.api-tutorial__book {
  display: grid;
  width: 42px;
  height: 42px;
  margin-right: 12px;
  place-items: center;
  color: #fff;
  border-radius: 11px;
  background: #246bfe;
  font-size: 23px;
}

.api-tutorial__heading h2 {
  margin: 0 0 5px;
  color: #111827;
  font-size: 20px;
  font-weight: 700;
}

.api-tutorial__heading p,
.api-tutorial__intro p,
.api-tutorial__step-title p,
.api-tutorial__finish p {
  margin: 0;
  color: #8790a3;
  font-size: 13px;
  line-height: 1.6;
}

.api-tutorial__close {
  width: 34px;
  height: 34px;
  padding: 0;
  color: #8c94a5;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 24px;
  line-height: 1;
}

.api-tutorial__close:hover { background: #f2f5f9; }

.api-tutorial__exchanges {
  display: grid;
  padding: 14px 24px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  background: #f7f9fc;
  border-bottom: 1px solid #e9edf4;
}

.api-tutorial__exchange {
  min-width: 0;
  height: 54px;
  padding: 7px 10px;
  text-align: left;
  color: #31384a;
  border: 1px solid #e1e6ef;
  border-radius: 10px;
  background: #fff;
}

.api-tutorial__exchange.active {
  color: #1255d8;
  border-color: #8db3ff;
  box-shadow: 0 0 0 2px rgba(36, 107, 254, .08);
}

.api-tutorial__exchange-logo {
  display: grid;
  width: 34px;
  height: 34px;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 9px;
  background: #f1f5fb;
}

.api-tutorial__exchange-logo img {
  display: block;
  width: 23px;
  height: 23px;
  object-fit: contain;
}

.api-tutorial__exchange > span:nth-child(2) {
  min-width: 0;
  margin-left: 9px;
  flex: 1;
}

.api-tutorial__exchange strong,
.api-tutorial__exchange small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.api-tutorial__exchange strong { font-size: 13px; }
.api-tutorial__exchange small { margin-top: 2px; color: #9aa2b2; font-size: 11px; }
.api-tutorial__exchange i { color: #a0a8b8; font-size: 11px; font-style: normal; white-space: nowrap; }
.api-tutorial__exchange.active i { color: #246bfe; }

.api-tutorial__body {
  display: grid;
  min-height: 0;
  flex: 1;
  grid-template-columns: 225px minmax(0, 1fr);
}

.api-tutorial__aside {
  padding: 20px 16px;
  overflow-y: auto;
  border-right: 1px solid #e9edf4;
  background: #fbfcfe;
}

.api-tutorial__current {
  padding: 0 4px 16px;
  margin-bottom: 10px;
  border-bottom: 1px solid #e9edf4;
}

.api-tutorial__current > div { margin-left: 10px; }
.api-tutorial__current small { display: block; color: #929bad; font-size: 11px; }
.api-tutorial__current strong { display: block; margin-top: 2px; color: #182033; font-size: 14px; }

.api-tutorial__aside > button {
  display: flex;
  width: 100%;
  min-height: 40px;
  padding: 7px 8px;
  align-items: center;
  text-align: left;
  color: #596174;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 12px;
}

.api-tutorial__aside > button:hover { color: #1f62e7; background: #eef4ff; }
.api-tutorial__aside > button span {
  display: grid;
  width: 23px;
  height: 23px;
  margin-right: 8px;
  flex: 0 0 auto;
  place-items: center;
  color: #246bfe;
  border-radius: 50%;
  background: #e8f0ff;
  font-size: 11px;
  font-weight: 700;
}

.api-tutorial__safe {
  padding: 12px;
  margin-top: 16px;
  color: #87530a;
  border: 1px solid #fae0ad;
  border-radius: 9px;
  background: #fff8e9;
}

.api-tutorial__safe strong { font-size: 12px; }
.api-tutorial__safe p { margin: 5px 0 0; font-size: 11px; line-height: 1.6; }

.api-tutorial__content {
  min-width: 0;
  padding: 22px 26px 34px;
  overflow-y: auto;
  scroll-behavior: smooth;
  background: #f5f7fb;
}

.api-tutorial__intro,
.api-tutorial__step,
.api-tutorial__finish {
  border: 1px solid #e2e7ef;
  border-radius: 12px;
  background: #fff;
}

.api-tutorial__intro {
  display: flex;
  padding: 18px 20px;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.api-tutorial__intro > div:first-child > span {
  color: #246bfe;
  font-size: 12px;
  font-weight: 700;
}

.api-tutorial__intro h3,
.api-tutorial__step h3,
.api-tutorial__finish h3 {
  margin: 4px 0 5px;
  color: #141a29;
  font-size: 16px;
}

.api-tutorial__permission {
  min-width: 245px;
  padding: 12px 14px;
  border-radius: 9px;
  background: #f2f6ff;
}

.api-tutorial__permission small { color: #7f899c; font-size: 11px; }
.api-tutorial__permission p { display: flex; margin-top: 7px; gap: 7px; }
.api-tutorial__permission b {
  padding: 3px 8px;
  color: #1165d7;
  border-radius: 12px;
  background: #e5efff;
  font-size: 11px;
}
.api-tutorial__permission b.disabled { color: #8b93a3; background: #e9ecf2; }

.api-tutorial__step {
  padding: 20px;
  margin-top: 14px;
  scroll-margin-top: 12px;
}

.api-tutorial__step-title { align-items: flex-start; }
.api-tutorial__step-title > span {
  min-width: 54px;
  padding: 5px 8px;
  margin: 2px 12px 0 0;
  text-align: center;
  color: #246bfe;
  border-radius: 14px;
  background: #eaf1ff;
  font-size: 11px;
  font-weight: 700;
}
.api-tutorial__step h3 { margin-top: 0; }

.api-tutorial__notice {
  padding: 9px 11px;
  margin: 14px 0 0;
  color: #8b5b0e;
  border-radius: 7px;
  background: #fff7e6;
  font-size: 12px;
  line-height: 1.6;
}

.api-tutorial__images {
  display: grid;
  margin-top: 16px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.api-tutorial__images figure {
  display: grid;
  min-height: 170px;
  padding: 10px;
  margin: 0;
  place-items: center;
  overflow: hidden;
  border: 1px solid #e1e6ee;
  border-radius: 9px;
  background: #fafbfc;
}

.api-tutorial__images img {
  display: block;
  width: 100%;
  max-height: 380px;
  object-fit: contain;
}

.api-tutorial__images figcaption { margin-top: 8px; color: #8e97a8; font-size: 11px; }

.api-tutorial__finish {
  padding: 17px 20px;
  margin-top: 14px;
  border-color: #b8d5ff;
  background: #f3f8ff;
}

.api-tutorial__finish > span {
  display: grid;
  width: 34px;
  height: 34px;
  margin-right: 12px;
  place-items: center;
  color: #fff;
  border-radius: 50%;
  background: #1b70ee;
  font-weight: 700;
}
.api-tutorial__finish h3 { margin-top: 0; }

.api-tutorial__footer {
  display: flex;
  min-height: 70px;
  padding: 13px 24px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid #e9edf4;
  background: #fff;
}

.api-tutorial__footer p { margin: 0; color: #8a93a4; font-size: 11.5px; }
.api-tutorial__footer p span { color: #13ad68; }
.api-tutorial__footer button {
  min-width: 180px;
  height: 40px;
  padding: 0 18px;
  color: #fff;
  border: 0;
  border-radius: 8px;
  background: #246bfe;
  font-size: 13px;
  font-weight: 700;
}
.api-tutorial__footer button:hover { background: #175ce2; }

.api-tutorial-fade-enter-active,
.api-tutorial-fade-leave-active { transition: opacity .18s ease; }
.api-tutorial-fade-enter,
.api-tutorial-fade-leave-to { opacity: 0; }

@media (max-width: 900px) {
  .api-tutorial__exchanges { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .api-tutorial__body { grid-template-columns: 180px minmax(0, 1fr); }
  .api-tutorial__intro { align-items: flex-start; flex-direction: column; }
  .api-tutorial__permission { width: 100%; }
}
</style>
